"use client";

import { ClockIcon } from "lucide-react";
import { useRef } from "react";
import {
  type TimeValue,
  useDateSegment,
  useLocale,
  useTimeField,
} from "react-aria";
import {
  type DateFieldState,
  type DateSegment as IDateSegment,
  useTimeFieldState,
} from "react-stately";
import { Toggle } from "./Toggle";
import { cn } from "@repo/shared/utils";

interface DateSegmentProps {
  segment: IDateSegment;
  state: DateFieldState;
}

const DateSegment = ({ segment, state }: DateSegmentProps) => {
  const ref = useRef(null);

  const {
    segmentProps: { ...segmentProps },
  } = useDateSegment(segment, state, ref);

  return (
    <div
      {...segmentProps}
      ref={ref}
      className={cn(
        "focus:rounded-[2px] focus:bg-accent focus:text-accent-foreground focus:outline-none",
        segment.type !== "literal" ? "px-[1px]" : "",
        segment.isPlaceholder ? "text-muted-foreground" : ""
      )}
    >
      {segment.text}
    </div>
  );
};

export const TimeField = ({
  className,
  disabled,
  disableHasTimeChange,
  hasTime = true,
  onHasTimeChange,
  ...props
}: {
  className?: string;
  disabled?: boolean;
  disableHasTimeChange?: boolean;
  hasTime?: boolean;
  onHasTimeChange?: (hasTime: boolean) => void;
  value: TimeValue | null;
  onChange: (value: TimeValue) => void;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const { locale } = useLocale();
  const state = useTimeFieldState({
    ...props,
    locale,
  });

  useTimeField({ ...props, "aria-label": "time" }, state, ref);

  return (
    <div
      aria-label="Time field"
      className={cn(
        "flex items-center space-x-2 mt-1",
        disabled ? "cursor-not-allowed opacity-70" : ""
      )}
    >
      {!disableHasTimeChange && (
        <Toggle
          disabled={disabled}
          pressed={hasTime}
          onPressedChange={onHasTimeChange}
          size="lg"
          variant="outline"
          aria-label="Toggle time"
        >
          <ClockIcon size="16px" />
        </Toggle>
      )}
      {hasTime && (
        <div
          aria-label="time"
          ref={ref}
          className={cn(
            "inline-flex h-10 w-full flex-1 rounded-md border bg-white border-input px-3 py-2 text-sm " +
              "ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 " +
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            className
          )}
        >
          {state.segments.map((segment, i) => (
            <DateSegment key={i} segment={segment} state={state} />
          ))}
        </div>
      )}
    </div>
  );
};
